import {
  types,
  getSnapshot,
  getParent,
  type SnapshotIn,
} from 'mobx-state-tree';
import Dependency from '../metamodel/dependency';
import Inheritance from '../metamodel/inheritance';
import { modelReference } from '../refHelper';
import type { IDependency } from '../metamodel/dependency';
import type { IInheritance } from '../metamodel/inheritance';
import type { INode } from './node';
import type { IEDG } from './edg';

/**
 * A vertex for a link. It has x and y coordinates.
 */
const Vertex = types.model('Vertex', {
  x: types.number,
  y: types.number,
});

type VertexSnap = SnapshotIn<typeof Vertex>;

/**
 * The Link instance interface.
 *
 * HAND-DECLARED (not `Instance<typeof Link>`) because Link sits on the EDG
 * recursion boundary: it is held in `IEDG.links` AND reaches `IEDG` back through
 * `get edg() => getParent(self, 2)`. Typing the cross-getter against `IEDG` plus
 * exposing this interface to `IEDG.links` lets TS cut the cycle via interfaces
 * rather than re-inferring `typeof Link`/`typeof EDG`.
 */
export interface ILink {
  readonly dependency: IDependency | undefined;
  readonly inheritance: IInheritance | undefined;
  readonly vertices: { x: number; y: number }[];
  readonly jsVertices: ReadonlyArray<{ readonly x: number; readonly y: number }>;
  readonly edg: IEDG;
  readonly sourceNode: INode | undefined;
  readonly targetNode: INode | undefined;
  readonly center: { x: number; y: number };
  readonly peers: ILink[];
  readonly hasPeers: boolean;
  setVertices(vertices: VertexSnap[]): void;
  clearVertices(): void;
  fixCollision(): void;
}

/**
 * A link in an EDG. This is the visual counterpart of a dependency. It has a
 * list of vertices.
 */
const Link = types
  .model('Link', {
    dependency: types.maybe(modelReference<typeof Dependency>(Dependency)),
    inheritance: types.maybe(modelReference<typeof Inheritance>(Inheritance)),
    vertices: types.optional(types.array(Vertex), []),
  })
  .views((self0) => {
    const self = self0 as unknown as ILink;
    return {
      // self.vertices is a MobX Observable Array, rappid/joint.js expects a
      // regular array
      get jsVertices() {
        return getSnapshot(self0.vertices);
      },
      // The EDG this link belongs to
      get edg(): IEDG {
        return getParent(self0, 2);
      },
      // The source node for the link
      get sourceNode(): INode | undefined {
        if (self.dependency != null) {
          return self.edg.findNode(self.dependency.master);
        } else if (self.inheritance != null) {
          return self.edg.findNode(self.inheritance.supertype);
        }
      },
      // The target node for the link
      get targetNode(): INode | undefined {
        if (self.dependency != null) {
          return self.edg.findNode(self.dependency.dependent);
        } else if (self.inheritance != null) {
          return self.edg.findNode(self.inheritance.subtype);
        }
      },
      get center() {
        return {
          x: (self.sourceNode!.center.x + self.targetNode!.center.x) / 2,
          y: (self.sourceNode!.center.y + self.targetNode!.center.y) / 2,
        };
      },
      // List of the links that have the same source and target
      // Including self!
      get peers(): ILink[] {
        return self.edg.links.filter(
          (l) => l.sourceNode === self.sourceNode && l.targetNode === self.targetNode
        );
      },
      // Are there any links with the same source and target (excluding self)
      get hasPeers() {
        return self.peers.length > 1;
      },
    };
  })
  .actions((self0) => {
    const self = self0 as unknown as ILink;
    return {
      setVertices(vertices: VertexSnap[]) {
        self0.vertices = vertices as unknown as typeof self0.vertices;
        self.fixCollision();
      },
      clearVertices() {
        self0.vertices = [] as unknown as typeof self0.vertices;
        self.fixCollision();
      },
      fixCollision() {
        if (self.vertices.length === 0) {
          // We don't have vertices, check if we have peers without vertices -> collision
          const collision = self.peers.some((p) => p !== self && p.vertices.length === 0);
          if (collision) {
            const myIndex = self.peers.indexOf(self);
            // For some reason myIndex is -1 when called from afterAttach
            const sequence = myIndex !== -1 ? myIndex : self.peers.length;
            const distance = (50 + (50 * sequence) / 2) * (sequence % 2 === 0 ? 1 : -1);
            self0.vertices = [
              {
                x: self.center.x + distance,
                y: self.center.y + distance,
              },
            ] as unknown as typeof self0.vertices;
          }
        }
      },
      afterAttach() {
        self.fixCollision();
      },
    };
  });

export default Link;
